import { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Stack, Button } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import { VibeCodeResponse } from '../types';
import { errorReporter } from '@/services/errorReportingService';
import useAlertStore from '@/stores/alertStore';

interface VibeCodeResultProps {
  result: VibeCodeResponse;
  noteTitle: string;
  onRegenerate: () => void;
  isSubmitting: boolean;
}

const VibeCodeResult = ({ result, noteTitle, onRegenerate, isSubmitting }: VibeCodeResultProps) => {
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const { showAlert } = useAlertStore();

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.prompt_result ?? '');
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      errorReporter.expected('Failed to copy vibe code prompt to clipboard', error, {
        feature: 'vibeCode',
        operation: 'copyToClipboard',
      });
      showAlert('Failed to copy to clipboard. Please try selecting and copying the text manually.', 'warning');
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Vibe Code Prompt for: {noteTitle}
        </Typography>

        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={onRegenerate}
          disabled={isSubmitting}
          sx={{ textTransform: 'none' }}
        >
          Regenerate
        </Button>
      </Stack>

      <Box
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
        }}
      >
        {/* Header bar with filename + copy button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            bgcolor: 'grey.100',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 500 }}>
            vibe-prompt.md
          </Typography>

          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5 }}>
              {copied ? (
                <CheckIcon sx={{ fontSize: 16 }} color="success" />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Raw markdown content */}
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            bgcolor: 'background.paper',
            overflow: 'auto',
            maxHeight: 500,
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'text.primary',
          }}
        >
          {result.prompt_result ?? ''}
        </Box>
      </Box>
    </Box>
  );
};

export default VibeCodeResult;
